import React, { useRef, useEffect, useState } from 'react';
import { ThunderboltOutlined } from '@ant-design/icons';

const COLORS = {
  primary: '#1a1a2e',
  surface: '#1e1e3a',
  text: '#e2e8f0',
  textSecondary: '#94a3b8',
  textMuted: '#64748b',
  emerald: '#10b981',
  emeraldBg: 'rgba(16,185,129,0.12)',
  amber: '#f59e0b',
  amberBg: 'rgba(245,158,11,0.12)',
  rose: '#ef4444',
  roseBg: 'rgba(239,68,68,0.12)',
  violet: '#8b5cf6',
  violetBg: 'rgba(139,92,246,0.12)',
  blue: '#3b82f6',
  blueBg: 'rgba(59,130,246,0.12)',
  glass: 'rgba(255,255,255,0.04)',
  glassBorder: 'rgba(255,255,255,0.08)',
};

const EVENT_CONFIG = {
  sent: {
    icon: '\u2705',
    label: 'Email sent to',
    color: COLORS.emerald,
    bg: COLORS.emeraldBg,
  },
  delivered: {
    icon: '\uD83D\uDCEC',
    label: 'Email delivered to',
    color: COLORS.blue,
    bg: COLORS.blueBg,
  },
  opened: {
    icon: '\uD83D\uDC41',
    label: 'Email opened by',
    color: '#06b6d4',
    bg: 'rgba(6,182,212,0.12)',
  },
  clicked: {
    icon: '\uD83D\uDD17',
    label: 'Link clicked by',
    color: COLORS.violet,
    bg: COLORS.violetBg,
  },
  replied: {
    icon: '\uD83D\uDCAC',
    label: 'Reply from',
    color: COLORS.amber,
    bg: COLORS.amberBg,
  },
  bounced: {
    icon: '\u274C',
    label: 'Bounced for',
    color: COLORS.rose,
    bg: COLORS.roseBg,
  },
};

const DEFAULT_EVENT = {
  icon: '\u26A1',
  label: 'Event for',
  color: COLORS.textSecondary,
  bg: 'rgba(255,255,255,0.06)',
};

const glassCard = {
  background: COLORS.glass,
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border: `1px solid ${COLORS.glassBorder}`,
  borderRadius: 12,
};

const formatTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diff = now - d;
  const secs = Math.floor(diff / 1000);
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);

  if (secs < 10) return 'Just now';
  if (secs < 60) return `${secs}s ago`;
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const pulseKeyframes = `
@keyframes liveFeedPulse {
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.015); }
  100% { opacity: 1; transform: scale(1); }
}
@keyframes liveFeedFadeIn {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}
`;

const LiveFeed = ({ events = [] }) => {
  const scrollRef = useRef(null);
  const [prevLength, setPrevLength] = useState(events.length);
  const [newItemIds, setNewItemIds] = useState(new Set());

  useEffect(() => {
    if (events.length > prevLength) {
      const newCount = events.length - prevLength;
      const ids = new Set();
      for (let i = 0; i < newCount; i++) {
        ids.add(events[i]?._id || events[i]?.id || `new-${i}`);
      }
      setNewItemIds(ids);
      const timer = setTimeout(() => setNewItemIds(new Set()), 2000);
      setPrevLength(events.length);
      return () => clearTimeout(timer);
    }
    setPrevLength(events.length);
  }, [events.length, prevLength, events]);

  if (events.length === 0) {
    return (
      <div style={{ ...glassCard, padding: 32, textAlign: 'center' }}>
        <ThunderboltOutlined style={{ fontSize: 28, color: COLORS.textMuted, marginBottom: 8 }} />
        <div style={{ color: COLORS.textSecondary, fontSize: 13 }}>
          No live events yet
        </div>
        <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 4 }}>
          Events will appear here in real-time as emails are processed
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...glassCard, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* Inject pulse animation */}
      <style>{pulseKeyframes}</style>

      {/* Header */}
      <div style={{
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        borderBottom: `1px solid ${COLORS.glassBorder}`,
      }}>
        <div style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: COLORS.emerald,
          boxShadow: `0 0 8px ${COLORS.emerald}`,
          animation: 'liveFeedPulse 2s ease-in-out infinite',
        }} />
        <ThunderboltOutlined style={{ color: COLORS.amber, fontSize: 14 }} />
        <span style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>Live Feed</span>
        <span style={{
          fontSize: 11,
          color: COLORS.textMuted,
          padding: '1px 8px',
          borderRadius: 5,
          background: 'rgba(255,255,255,0.06)',
          marginLeft: 'auto',
        }}>
          {events.length} events
        </span>
      </div>

      {/* Scrollable Event List */}
      <div
        ref={scrollRef}
        style={{
          maxHeight: 500,
          overflowY: 'auto',
          padding: '4px 0',
        }}
      >
        {events.map((event, idx) => {
          const eventKey = event._id || event.id || idx;
          const config = EVENT_CONFIG[event.type || event.eventType] || DEFAULT_EVENT;
          const isNew = newItemIds.has(eventKey);

          return (
            <div
              key={eventKey}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 16px',
                borderBottom: `1px solid ${COLORS.glassBorder}`,
                background: isNew ? config.bg : 'transparent',
                animation: isNew ? 'liveFeedFadeIn 0.4s ease-out' : 'none',
                transition: 'background 0.3s ease',
              }}
            >
              {/* Timestamp */}
              <span style={{
                fontSize: 10,
                color: COLORS.textMuted,
                minWidth: 55,
                fontFamily: 'monospace',
                fontWeight: 500,
              }}>
                {formatTime(event.timestamp || event.at || event.createdAt)}
              </span>

              {/* Icon */}
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 7,
                background: config.bg,
                fontSize: 14,
                flexShrink: 0,
              }}>
                {config.icon}
              </span>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 12, color: COLORS.textSecondary }}>
                  {config.label}{' '}
                </span>
                <span style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: COLORS.text,
                }}>
                  {event.contactName || event.name || event.contactEmail || event.email || 'Unknown'}
                </span>
                {event.agencyName && (
                  <span style={{
                    fontSize: 10,
                    color: COLORS.amber,
                    marginLeft: 6,
                    padding: '1px 5px',
                    borderRadius: 3,
                    background: COLORS.amberBg,
                    fontWeight: 600,
                  }}>
                    {event.agencyName}
                  </span>
                )}
                {event.leadId && (
                  <span style={{
                    fontSize: 10,
                    color: COLORS.violet,
                    marginLeft: 4,
                    padding: '1px 5px',
                    borderRadius: 3,
                    background: COLORS.violetBg,
                    fontFamily: 'monospace',
                    fontWeight: 500,
                  }}>
                    ID: {event.leadId}
                  </span>
                )}
                {event.step && (
                  <span style={{ fontSize: 11, color: COLORS.textMuted, marginLeft: 6 }}>
                    Step {event.step}
                  </span>
                )}
                {event.details && (
                  <span style={{ fontSize: 11, color: COLORS.textMuted, marginLeft: 6 }}>
                    - {event.details}
                  </span>
                )}
              </div>

              {/* Event type tag */}
              <span style={{
                fontSize: 10,
                fontWeight: 600,
                color: config.color,
                padding: '2px 6px',
                borderRadius: 4,
                background: config.bg,
                textTransform: 'capitalize',
                flexShrink: 0,
              }}>
                {event.type || event.eventType || 'event'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LiveFeed;
